/**
 * Push Notification Service
 * Handles service worker registration, push subscription, and permission management
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

const getToken = () => localStorage.getItem('dockmate_token');

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Register the service worker
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Convert a base64 URL-safe string to a Uint8Array (needed for applicationServerKey)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Fetch the VAPID public key from the backend
 */
async function getVapidKey() {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}/notifications/push/vapid-key`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json();
  return data.key;
}

/**
 * Subscribe to push notifications
 * - Registers service worker
 * - Requests notification permission
 * - Creates push subscription
 * - Sends subscription to backend
 */
export async function subscribeToPush() {
  if (!isPushSupported()) return false;

  try {
    const registration = await registerServiceWorker();
    if (!registration) return false;

    // Wait for the service worker to be ready
    const sw = await navigator.serviceWorker.ready;

    // Check/request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    // Get VAPID key from backend
    const vapidKey = await getVapidKey();
    if (!vapidKey) return false;

    // Subscribe to push
    const subscription = await sw.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    // Send subscription to backend
    const token = getToken();
    const subJSON = subscription.toJSON();

    await fetch(`${API_BASE_URL}/notifications/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: subJSON.endpoint,
        keys: {
          p256dh: subJSON.keys.p256dh,
          auth: subJSON.keys.auth,
        },
      }),
    });

    return true;
  } catch (error) {
    console.error('Push subscription failed:', error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const endpoint = subscription.endpoint;

      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove from backend
      const token = getToken();
      if (token) {
        await fetch(`${API_BASE_URL}/notifications/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ endpoint }),
        });
      }
    }
  } catch (error) {
    console.error('Push unsubscribe failed:', error);
  }
}
